import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { apiFetch } from '@/lib/api';
import { closedWorldCheckMessage, closedWorldCheckPending } from '@/lib/analysisGate';
import { useReadiness, type ClosedWorldValidity } from '@/lib/useReadiness';
import { coverage, useAnswerability } from '@/lib/useAnswerability';
import { cn } from '@/lib/utils';
import { messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';
import { AnswerabilitySection } from '@/components/AnswerabilitySection';

/**
 * Readiness — how ready the ontology, and the data against it, are for querying and
 * grounding answers. A slide-in panel opened from the Ontology page. Analyzes
 * the whole dataset (schema + data across all graphs): instance counts are
 * global, so a single-graph scope would be incoherent. A future multi-graph
 * select will compose evaluations from chosen graph sets.
 */
export function ReadinessPanel({
  open,
  onClose,
  canEdit = false,
}: {
  open: boolean;
  onClose: () => void;
  /** Whether the signed-in role may record redundancy verdicts (schema-edit
   *  authority — the server enforces this regardless). */
  canEdit?: boolean;
}) {
  const [showHelp, setShowHelp] = useState(false);
  const [verdictError, setVerdictError] = useState<string | null>(null);
  const auth = useAuth();
  const query = useReadiness(null);
  const m = query.data;
  const lastCheck = useAnswerability(auth.user?.id_token).data?.last ?? null;
  const ontologyScore = coverage(lastCheck?.report);
  const check = m?.closed_world_check ?? null;
  const checkPending = closedWorldCheckPending(check);
  const checkMessage = check
    ? closedWorldCheckMessage(check, (iso) => new Date(iso).toLocaleTimeString())
    : null;

  const verdict = async (method: 'POST' | 'DELETE', a_uri: string, b_uri: string) => {
    const token = auth.user?.id_token;
    if (!token) {
      setVerdictError('Not signed in — the verdict was not recorded.');
      return;
    }
    setVerdictError(null);
    const r = await apiFetch('/api/v1/vdb/ontology-readiness/dismissals', token, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ a_uri, b_uri }),
    });
    if (!r.ok) {
      setVerdictError(r.message);
      return;
    }
    void query.refetch();
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} aria-hidden />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-border bg-card shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-tres-text-primary">Readiness</h2>
            <p className="text-xs text-tres-text-secondary">
              How ready your ontology, and the data against it, are for querying and grounding
              answers. It covers every graph, not only the one selected in the editor.{' '}
              <button
                type="button"
                onClick={() => setShowHelp(true)}
                className={buttonVariants({ variant: 'link', size: 'md' })}
              >
                About these metrics →
              </button>
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
              title="Re-run the readiness analysis (scoring does not update on its own after edits)"
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              {query.isFetching ? 'Scoring…' : 'Refresh'}
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={buttonVariants({ variant: 'ghost', size: 'icon' })}
            >
              ×
            </button>
          </div>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <AnswerabilitySection token={auth.user?.id_token} />
          {query.isLoading && (
            <div className="rounded-lg border border-border bg-card p-6 text-sm text-tres-text-secondary">
              Analyzing…
            </div>
          )}
          {query.error && (
            <div className={messageClass('warning', 'normal')}>
              {(query.error as Error).message}
            </div>
          )}
          {m && (
            <>
              {m.index?.state === 'building' && (
                <div className={messageClass('platform', 'compact', 'warning')}>
                  The knowledge index is rebuilding after recent edits — these results reflect the
                  previous snapshot. The panel will refresh itself when the rebuild completes
                  (started {new Date(m.index.started_at).toLocaleTimeString()}).
                </div>
              )}
              {m.index?.state === 'failed' && (
                <div className={messageClass('warning', 'compact')}>
                  The last index rebuild failed — these results may be stale. {m.index.error}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="text-3xl font-semibold tabular-nums text-tres-text-primary">
                    {ontologyScore === null ? '—' : `${Math.round(ontologyScore * 100)}%`}
                  </div>
                  <div className="mt-1 text-sm font-medium text-tres-text-primary">
                    Ontology readiness
                  </div>
                  <div className="text-xs text-tres-text-secondary">
                    {lastCheck
                      ? `The share of ${lastCheck.report.bank_name} the ontology can answer, from the check above.`
                      : 'Not scored yet: run a question set through the check above.'}
                  </div>
                </div>
                {check && (
                  <div className="rounded-lg border border-border bg-card p-4">
                    <div className="text-3xl font-semibold tabular-nums text-tres-text-primary">
                      {checkPending || !m.closed_world
                        ? '…'
                        : `${Math.round(m.closed_world.valid_fraction * 100)}%`}
                    </div>
                    <div className="mt-1 text-sm font-medium text-tres-text-primary">
                      Data readiness
                    </div>
                    <div className="text-xs text-tres-text-secondary">
                      The share of facts that do not conflict with the ontology, read across the
                      whole store.
                    </div>
                  </div>
                )}
              </div>
              {checkMessage && (
                <div
                  role="status"
                  className={cn(
                    'rounded-lg border bg-card p-3 text-xs',
                    check?.state === 'failed'
                      ? 'border-tres-error text-tres-error'
                      : 'border-border text-tres-text-secondary',
                  )}
                >
                  {checkMessage}
                </div>
              )}
              <p className="text-sm text-tres-text-secondary">
                {m.class_count} class{m.class_count === 1 ? '' : 'es'} · {m.property_count} propert
                {m.property_count === 1 ? 'y' : 'ies'}
              </p>

              <Section title="Annotation density">
                <MetricBar label="Classes with a label" frac={m.class_label_coverage} />
                <MetricBar
                  label="Classes documented (comment / definition)"
                  frac={m.class_doc_coverage}
                />
                <MetricBar label="Properties with a label" frac={m.property_label_coverage} />
                <MetricBar label="Properties documented" frac={m.property_doc_coverage} />
              </Section>

              {(m.redundancy_candidates.length > 0 ||
                (m.dismissed_redundancy?.length ?? 0) > 0) && (
                <Section title="Possible redundant classes">
                  {verdictError && (
                    <p className={messageClass('warning', 'compact')} role="alert">
                      {verdictError}
                    </p>
                  )}
                  <p className="text-xs text-tres-text-secondary">
                    Same head noun, no hierarchy between them — often the same concept declared
                    twice. Review and merge (Ontology Editor → Merge into), or mark a pair Not
                    redundant to keep it off this list.
                  </p>
                  <ul className="mt-1 space-y-1">
                    {m.redundancy_candidates.map((c) => (
                      <li
                        key={`${c.a_uri}|${c.b_uri}`}
                        className={cn(messageClass('platform', 'compact', 'warning'), 'flex items-center justify-between gap-2')}
                      >
                        <span>
                          <span className="font-medium">{c.a_label}</span>
                          <span className="text-tres-text-secondary"> vs </span>
                          <span className="font-medium">{c.b_label}</span>
                          <span className="ml-1 text-tres-text-secondary">({c.shared_head})</span>
                        </span>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => void verdict('POST', c.a_uri, c.b_uri)}
                            title="Record that these two classes are distinct concepts — the pair stays off this list until restored"
                            className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'shrink-0')}
                          >
                            Not redundant
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                  {(m.dismissed_redundancy?.length ?? 0) > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-tres-text-secondary">
                        Reviewed as not redundant ({m.dismissed_redundancy.length})
                      </summary>
                      <ul className="mt-1 space-y-1">
                        {m.dismissed_redundancy.map((c) => (
                          <li
                            key={`${c.a_uri}|${c.b_uri}`}
                            className="flex items-center justify-between gap-2 rounded border border-border px-2 py-1 text-xs text-tres-text-secondary"
                          >
                            <span>
                              {c.a_label} vs {c.b_label}
                            </span>
                            {canEdit && (
                              <button
                                type="button"
                                onClick={() => void verdict('DELETE', c.a_uri, c.b_uri)}
                                title="Return this pair to the review list"
                                className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'shrink-0')}
                              >
                                Restore
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </Section>
              )}
              <Section title="Structure & coverage">
                <MetricBar
                  label="Properties declaring domain & range"
                  frac={m.domain_range_completeness}
                  detail={`${m.orphan_property_count} orphan${m.orphan_property_count === 1 ? '' : 's'}`}
                />
                <MetricBar
                  label="Declared properties carrying data"
                  frac={m.property_instantiation_coverage}
                  detail={`${m.declared_unused_property_count} unused`}
                />
                <div className="flex flex-wrap gap-6 pt-1">
                  <Stat
                    label="isolated classes"
                    value={m.isolated_class_count}
                    warn={m.isolated_class_count > 0}
                  />
                  <Stat
                    label="connected components"
                    value={m.connected_components}
                    warn={m.connected_components > 1}
                  />
                </div>
              </Section>

              {(m.declared_unused_properties.length > 0 ||
                m.undeclared_predicates.length > 0) && (
                <Section title="Declared vs used">
                  {m.declared_unused_properties.length > 0 && (
                    <>
                      <p className="text-xs text-tres-text-secondary">
                        Declared but carrying no data — retrieval on these can only come back
                        empty ({m.declared_unused_property_count} total).
                      </p>
                      <ul className="mt-1 space-y-1">
                        {m.declared_unused_properties.map((p) => (
                          <li
                            key={p.uri}
                            className={messageClass('platform', 'compact', 'warning')}
                            title={p.uri}
                          >
                            {p.label}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {m.undeclared_predicates.length > 0 && (
                    <>
                      <p className="mt-2 text-xs text-tres-text-secondary">
                        Used in the data but never declared — no domain or range to ground
                        against ({m.undeclared_predicate_count} total).
                      </p>
                      <ul className="mt-1 space-y-1">
                        {m.undeclared_predicates.map((p) => (
                          <li
                            key={p.uri}
                            className={cn(messageClass('platform', 'compact', 'warning'), 'flex items-center justify-between gap-2')}
                            title={p.uri}
                          >
                            <span>{p.label}</span>
                            <span className="shrink-0 text-tres-text-secondary">
                              {p.usage_count} use{p.usage_count === 1 ? '' : 's'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </Section>
              )}

              {m.unfinished_classes.length > 0 && (
                <Section title="Declared intent, unfinished">
                  <p className="text-xs text-tres-text-secondary">
                    Classes declared but missing several completeness signals — each absence
                    named with its effect on grounding.
                  </p>
                  <ul className="mt-1 space-y-1">
                    {m.unfinished_classes.map((c) => (
                      <li
                        key={c.uri}
                        className={messageClass('platform', 'compact', 'warning')}
                        title={c.uri}
                      >
                        <span className="font-medium">{c.label}</span>
                        <ul className="mt-0.5 space-y-0.5">
                          {c.missing.map((s) => (
                            <li key={s.what} className="text-tres-text-secondary">
                              missing {s.what}: {s.effect}
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              <DataSection
                classCoverage={m.class_instantiation_coverage}
                emptyClasses={m.uninstantiated_class_count}
                cw={m.closed_world ?? null}
              />
            </>
          )}
        </div>
      </aside>

      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-tres-text-secondary">
        {title}
      </h3>
      {children}
    </section>
  );
}

function MetricBar({ label, frac, detail }: { label: string; frac: number; detail?: string }) {
  const pct = Math.round(frac * 100);
  const color = frac >= 0.8 ? 'bg-tres-success' : frac >= 0.5 ? 'bg-tres-action' : 'bg-tres-warning';
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-tres-text-primary">{label}</span>
        <span className="shrink-0 tabular-nums text-tres-text-secondary">
          {pct}%{detail ? ` · ${detail}` : ''}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full', color)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span
        className={cn(
          'text-lg font-semibold tabular-nums',
          warn ? 'text-tres-warning' : 'text-tres-text-primary',
        )}
      >
        {value}
      </span>
      <span className="text-xs text-tres-text-secondary">{label}</span>
    </span>
  );
}

/**
 * The data measured against the ontology. Classes with instances is a curation
 * figure on both backends and no part of a score. On QROS the section adds the
 * closed-world verdict V(a) over the whole store, which is data readiness: the
 * share of facts the ontology's rules accept, the count per rule that rejects
 * the rest, and a few examples (open-world RDF cannot rule a fact out).
 */
function DataSection({
  classCoverage,
  emptyClasses,
  cw,
}: {
  classCoverage: number;
  emptyClasses: number;
  cw: ClosedWorldValidity | null;
}) {
  return (
    <Section title="Data">
      <MetricBar
        label="Classes with instances"
        frac={classCoverage}
        detail={`${emptyClasses.toLocaleString()} with no instances yet · for curation, not part of a score`}
      />
      {cw && (
        <MetricBar
          label="Facts that do not conflict with the ontology (data readiness)"
          frac={cw.valid_fraction}
          detail={
            cw.falsified === 0
              ? 'no fact conflicts with the ontology'
              : `${cw.falsified.toLocaleString()} of ${cw.statements_checked.toLocaleString()} conflict with the ontology`
          }
        />
      )}
      {cw && cw.by_rule.length > 0 && (
        <p className="pt-1 text-xs text-tres-text-secondary">Rules that reject facts</p>
      )}
      {cw && cw.by_rule.length > 0 && (
        <div className="space-y-1 pt-1">
          {cw.by_rule.map((r) => (
            <div
              key={r.rule_id}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <span className="text-tres-text-primary">{r.rule_id}</span>
              <span className="shrink-0 tabular-nums text-tres-warning">{r.count}</span>
            </div>
          ))}
        </div>
      )}
      {cw && cw.samples.length > 0 && (
        <ul className="space-y-1.5 pt-1">
          {cw.samples.slice(0, 5).map((s, i) => (
            <li key={i} className="text-xs text-tres-text-secondary">
              {s.message}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ---- explanatory documentation (linked from the panel) ----

function HelpModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-5 text-sm shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-tres-text-primary">About ontology readiness</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'shrink-0')}
          >
            ×
          </button>
        </div>

        <div className="mt-3 space-y-4 text-tres-text-secondary">
          <p>
            Readiness gives two scores. Ontology readiness is the share of a question set the
            ontology can answer, from Can it answer your questions?: each question is read against
            the ontology and the site&apos;s rules, and what the set lacks is listed, the classes and
            properties first, then the rules required for facts no data states. Data readiness reads
            the data against the ontology: the share of facts that do not conflict with it. The
            measures below explain a low score and do not enter either one. Each says what it looks
            at and how to improve it.
          </p>

          <Doc title="Annotation density">
            The fraction of classes and properties carrying a human-readable label and documentation
            (a comment or definition). Labels and comments are what a model reads to map a question's
            words onto the schema's real terms. Higher density means the model uses the dataset's real
            terms instead of inventing plausible-but-wrong ones. <strong>Improve:</strong> add a label
            and a comment to every class and property.
          </Doc>
          <Doc title="Domain & range completeness">
            The fraction of properties that declare both a domain (the class they start from) and a
            range (the class or datatype they point to). Properties missing either are "orphans" —
            they don't tell a query what connects to what, which weakens discoverability.{' '}
            <strong>Improve:</strong> declare a domain and range on every property.
          </Doc>
          <Doc title="Structure — isolated classes & connected components">
            The class graph is built from declared relations: property domain/range edges and the
            subclass hierarchy. An <strong>isolated class</strong> is touched by no declared
            relation, so nothing connects to it through the schema. <strong>Connected components</strong>{' '}
            count the separate "islands": one component means every class is reachable from every
            other; more than one means the schema is fragmented and questions that span the gaps can't
            be answered. <strong>Improve:</strong> add properties (with domain/range) or subclass
            links that bridge the islands.
          </Doc>

          <Doc title="Data readiness: facts that conflict with the ontology (QROS)">
            A fact conflicts with the ontology when it breaks a rule the ontology states: a property
            used on a kind of thing outside its domain or range, a value outside its allowed list,
            more values than the property allows, or a time window that ends before it starts. This
            is the share of facts that conflict with none of its rules, with a count for each rule
            the rest break and a few examples. RDF cannot rule a fact out, so this reading appears
            only on QROS. Unlike the measures above, it reads the data itself, not just the schema.{' '}
            <strong>Improve:</strong> widen a property's domain or range to fit how it is really
            used, or correct the facts that are wrong. Each fix lets querying and grounding rely on
            those facts again.
          </Doc>

          <Doc title="Classes with instances (for curation)">
            The fraction of declared classes that have instances in the data. It is a curation
            figure, not part of either score: an ontology in development has classes no data uses
            yet, and that does not make it less ready. Read it to find classes you expected data
            for, or classes no longer in use.
          </Doc>

          <p className="text-xs">
            These are structural predictors. The data-preparedness evaluation measures the actual
            effect on an LLM's answers (with vs. without the schema as context) and correlates it with
            these scores.
          </p>
        </div>
      </div>
    </div>
  );
}

function Doc({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <h3 className="font-semibold text-tres-text-primary">{title}</h3>
      <p>{children}</p>
    </div>
  );
}

